/**
 * Hook para gerenciamento inteligente de profiles
 * Reutilizável para Individual e LegalPerson
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProfileStorageManager, EntityType } from '../utils/profileStorageManager';

interface UseProfileLoaderReturn {
    profiles: string[];
    loadProfiles: () => Promise<string[]>;
    saveProfiles: (profiles: string[]) => void;
    clearProfiles: () => void;
    isLoading: boolean;
}

export const useProfileLoader = (
    entityType: EntityType, 
    entityId: string,
    loadFromDatabase?: () => Promise<string[]>
): UseProfileLoaderReturn => {
    const [profiles, setProfiles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams] = useSearchParams();

    /**
     * Carrega profiles seguindo prioridade: localStorage → query string → database
     */
    const loadProfiles = useCallback(async (): Promise<string[]> => {
        setIsLoading(true);
        let loadedProfiles: string[] = [];

        try {
            // 1. PRIORIDADE: localStorage
            loadedProfiles = ProfileStorageManager.load(entityType, entityId);
            
            if (loadedProfiles.length > 0) {
                console.log('✅ Profiles carregados do localStorage');
                setProfiles(loadedProfiles);
                return loadedProfiles;
            }

            // 2. FALLBACK: Query string
            const urlProfiles = searchParams.get('profiles');
            if (urlProfiles && urlProfiles.trim()) {
                loadedProfiles = urlProfiles.split(',').filter(p => p.trim());
                if (loadedProfiles.length > 0) {
                    console.log('✅ Profiles carregados da query string');
                    // Salvar em localStorage para próximas vezes
                    ProfileStorageManager.save(entityType, entityId, loadedProfiles);
                    setProfiles(loadedProfiles);
                    return loadedProfiles;
                }
            }

            // 3. ÚLTIMO RECURSO: Database
            if (loadFromDatabase) {
                try {
                    const dbProfiles = await loadFromDatabase();
                    if (dbProfiles && dbProfiles.length > 0) {
                        loadedProfiles = dbProfiles;
                        // Salvar em localStorage
                        ProfileStorageManager.save(entityType, entityId, loadedProfiles);
                        setProfiles(loadedProfiles);
                        return loadedProfiles;
                    }
                } catch (error) {
                    console.warn('⚠️ Erro ao carregar profiles do banco:', error);
                }
            }

            // 🚨 FIX: Só logar e atualizar se realmente mudou para evitar loop infinito
            if (profiles.length > 0) {
                console.log('ℹ️ Nenhum profile encontrado - limpando profiles');
                setProfiles([]);
            }
            return [];

        } finally {
            setIsLoading(false);
        }
    }, [entityType, entityId, searchParams, loadFromDatabase]);

    /**
     * Salva profiles no localStorage e estado
     */
    const saveProfiles = useCallback((newProfiles: string[]) => {
        ProfileStorageManager.save(entityType, entityId, newProfiles);
        setProfiles(newProfiles);
        console.log(`💾 Profiles salvos: ${newProfiles.join(', ')}`);
    }, [entityType, entityId]);

    /**
     * Limpa profiles do localStorage e estado
     */
    const clearProfiles = useCallback(() => {
        ProfileStorageManager.clear(entityType, entityId);
        setProfiles([]);
        console.log('🗑️ Profiles limpos');
    }, [entityType, entityId]);

    /**
     * Cleanup automático ao desmontar
     */
    useEffect(() => {
        return () => {
            // Limpeza automática de dados antigos
            ProfileStorageManager.cleanup();
        };
    }, []);

    /**
     * Carregamento automático na inicialização
     */
    useEffect(() => {
        if (entityId) {
            loadProfiles();
        }
    }, [entityId, loadProfiles]);

    return {
        profiles,
        loadProfiles,
        saveProfiles,
        clearProfiles,
        isLoading
    };
};
